import type { StoreApi } from "zustand/vanilla";
import type { VoiceAgentStore, BubbleColorInput } from "@moss-js/founding-agent/core";
import {
  STATE_STATUS,
  dismissTeaser,
  displayStateToConversationState,
  isTeaserDismissed,
  loadTeaserFont,
  resolveBubbleColor,
} from "@moss-js/founding-agent/core";
import type { AgentAppearance, TeaserConfig } from "../config.js";
import { createAura } from "./aura.js";
import { CHAT_PANEL_ID, createChatState, type ChatState } from "./chat-state.js";
import { icon } from "./icons.js";
import { createOrbUI, type OrbUIHandle } from "./orb.js";

const BUBBLE_SIZE = 56;
const PILL_WIDTH = 308;

export interface BubbleOpts {
  color: BubbleColorInput;
  /** Explicit theme. Omitted = fetched appearance's widget_theme, then light. */
  themeMode?: "light" | "dark";
  /** Explicit appearance. Pins both the orb AND the theme (it carries its own
   *  widget_theme), so fetched config overrides neither. */
  appearance?: AgentAppearance | null;
  label?: string;
  /** Chat-open state shared with the transcript panel; the pill's toggle
   *  writes it. A private one is used when omitted. */
  chat?: ChatState;
  /** Keys the teaser's per-session dismissal. */
  publishableKey?: string;
  open: () => Promise<void>;
  end: () => void;
  toggleMute: () => void;
}

export interface BubbleHandle {
  /** Apply the fetched portal appearance. No-op when an explicit
   *  `appearance` was passed at construction (explicit wins). */
  setAppearance(appearance: AgentAppearance | null): void;
  /** Apply the fetched widget_theme. No-op when an explicit `themeMode` OR an
   *  explicit `appearance` was passed at construction. */
  setTheme(theme: "light" | "dark" | undefined): void;
  /** Apply the fetched portal teaser; `null` hides it. */
  setTeaser(teaser: TeaserConfig | null): void;
  destroy(): void;
}

export function createBubble(
  root: HTMLElement,
  store: StoreApi<VoiceAgentStore>,
  opts: BubbleOpts,
): BubbleHandle {
  const label = opts.label ?? "Talk to the agent";
  const chat = opts.chat ?? createChatState();
  // An explicit appearance carries its own widget_theme, so it pins the theme
  // too. Keying this off themeMode alone meant init({ appearance: dark }) kept
  // the dark orb but let a fetched light appearance flip the chrome to light.
  const themePinned = opts.themeMode != null || opts.appearance != null;
  const appearancePinned = opts.appearance != null;

  let appearance: AgentAppearance | null = opts.appearance ?? null;
  let theme: "light" | "dark" =
    opts.themeMode ?? appearance?.widget_theme ?? "light";

  // "built on moss" label, shown above the live pill.
  const attribution = document.createElement("div");
  attribution.setAttribute("data-moss-attribution", "");
  attribution.setAttribute("aria-hidden", "true");
  attribution.append("built on ", Object.assign(document.createElement("span"), { textContent: "moss" }));
  attribution.hidden = true;
  root.appendChild(attribution);

  // Portal teaser directly above the idle pill: click to talk, X to close.
  // Either retires it for the rest of the browser session.
  const publishableKey = opts.publishableKey ?? "";
  let teaser: TeaserConfig | null = null;
  let teaserClosed = false;
  const teaserEl = document.createElement("div");
  teaserEl.setAttribute("data-moss-teaser", "");
  teaserEl.hidden = true;
  const teaserText = document.createElement("button");
  teaserText.type = "button";
  teaserText.setAttribute("data-moss-teaser-text", "");
  const teaserClose = document.createElement("button");
  teaserClose.type = "button";
  teaserClose.setAttribute("data-moss-teaser-close", "");
  teaserClose.setAttribute("aria-label", "Dismiss");
  teaserClose.title = "Dismiss";
  teaserClose.replaceChildren(icon("x", 14));
  teaserEl.append(teaserText, teaserClose);
  root.appendChild(teaserEl);

  // Pill/circle container. Figma order: avatar leads on the left, then
  // [status | chat | mic | end] once live.
  const container = document.createElement("div");
  container.setAttribute("data-moss-bubble", "");
  container.style.width = `${BUBBLE_SIZE}px`;
  root.appendChild(container);

  // Avatar button (always leftmost)
  const avatarBtn = document.createElement("button");
  avatarBtn.type = "button";
  avatarBtn.setAttribute("data-moss-avatar", "");
  avatarBtn.setAttribute("aria-label", label);
  avatarBtn.title = label;
  container.appendChild(avatarBtn);

  // Orb when an appearance exists, else the preset-color aura.
  let avatarHandle: { destroy(): void } & Partial<Pick<OrbUIHandle, "setAppearance">> =
    appearance
      ? createOrbUI(avatarBtn, store, { appearance })
      : createAura(avatarBtn, store, { color: opts.color, themeMode: theme });

  // Pulsing ring around the avatar while connecting.
  const ring = document.createElement("span");
  ring.setAttribute("data-moss-connecting-ring", "");
  ring.setAttribute("aria-hidden", "true");
  ring.hidden = true;
  avatarBtn.appendChild(ring);

  // Controls area (shown when connected)
  const controls = document.createElement("div");
  controls.setAttribute("data-moss-controls", "");
  controls.hidden = true;
  container.appendChild(controls);

  // Status line ("Listening ..." / "Speaking ...")
  const status = document.createElement("span");
  status.setAttribute("data-moss-status", "");
  status.setAttribute("aria-live", "polite");
  controls.appendChild(status);

  // Chat toggle
  const toggleBtn = document.createElement("button");
  toggleBtn.type = "button";
  toggleBtn.setAttribute("data-moss-chat-toggle", "");
  toggleBtn.setAttribute("aria-controls", CHAT_PANEL_ID);
  toggleBtn.replaceChildren(icon("message-square"));
  controls.appendChild(toggleBtn);

  // Mute button
  const muteBtn = document.createElement("button");
  muteBtn.type = "button";
  muteBtn.setAttribute("data-moss-mute", "");
  controls.appendChild(muteBtn);

  // End button (circular red X)
  const endBtn = document.createElement("button");
  endBtn.type = "button";
  endBtn.setAttribute("data-moss-end", "");
  endBtn.setAttribute("aria-label", "End conversation");
  endBtn.title = "End (Esc)";
  endBtn.replaceChildren(icon("x"));
  controls.appendChild(endBtn);

  // The theme is stamped on the root so the chat panel (a sibling of the pill
  // in the same column) picks up the same variant from the stylesheet.
  function applyTheme(): void {
    root.setAttribute("data-moss-theme", theme);
    const ringColor = appearance ? appearance.colors[0] : resolveBubbleColor(opts.color);
    ring.style.boxShadow = `0 0 0 2px ${ringColor}66`;
    root.style.setProperty("--moss-teaser-accent", ringColor);
  }

  function paintToggle(open: boolean): void {
    toggleBtn.toggleAttribute("data-open", open);
    toggleBtn.setAttribute("aria-expanded", String(open));
    toggleBtn.setAttribute("aria-label", open ? "Hide chat" : "Show chat");
    toggleBtn.title = open ? "Hide chat" : "Type instead";
  }

  let paintedMute: boolean | null = null;
  function paintMute(muted: boolean): void {
    if (paintedMute === muted) return;
    paintedMute = muted;
    muteBtn.replaceChildren(icon(muted ? "mic-off" : "mic"));
    muteBtn.toggleAttribute("data-muted", muted);
    muteBtn.setAttribute("aria-label", muted ? "Unmute mic" : "Mute mic");
    muteBtn.title = muted ? "Unmute mic (M)" : "Mute mic (M)";
  }

  // Mirrors React's effect keyed on isConnected: close the chat on mount if
  // not connected and whenever the call leaves connected (manual end() and
  // network drops alike), not on every store write.
  let prevConnected: boolean | null = null;

  function render(state: ReturnType<typeof store.getState>): void {
    const connected = state.connectionState === "connected";
    const connecting = state.connectionState === "connecting";

    container.style.width = `${connected ? PILL_WIDTH : BUBBLE_SIZE}px`;
    controls.hidden = !connected;
    attribution.hidden = !connected;
    // Any accepted start retires it: this bubble, the public open(), or a
    // call begun before /config delivered the teaser.
    if ((connecting || connected) && !teaserClosed) retireTeaser();
    const teaserVisible = !!(
      state.connectionState === "idle" &&
      teaser &&
      !teaserClosed &&
      !isTeaserDismissed(publishableKey)
    );
    // The font is fetched only for a teaser that actually shows.
    if (teaserVisible) loadTeaserFont();
    teaserEl.hidden = !teaserVisible;
    ring.hidden = !connecting;
    avatarBtn.disabled = connecting || connected;

    if (connected) {
      const convo = displayStateToConversationState(state.agentDisplayState);
      status.textContent = STATE_STATUS[convo === "idle" ? "listening" : convo];
    } else if (prevConnected !== false) {
      chat.setOpen(false);
    }
    prevConnected = connected;

    paintMute(state.isMicMuted);
  }

  applyTheme();
  paintToggle(chat.open);
  const unsubChat = chat.subscribe(paintToggle);
  render(store.getState());

  const unsub = store.subscribe((state) => render(state));

  function retireTeaser(): void {
    dismissTeaser(publishableKey);
    teaserClosed = true;
    teaserEl.hidden = true;
  }

  function start(): void {
    const { connectionState } = store.getState();
    if (connectionState !== "idle" && connectionState !== "error") return;
    retireTeaser();
    void opts.open();
  }

  avatarBtn.addEventListener("click", start);
  // The whole card starts the conversation; the X only closes it.
  teaserEl.addEventListener("click", (event) => {
    if (!teaserClose.contains(event.target as Node)) start();
  });
  teaserClose.addEventListener("click", retireTeaser);

  toggleBtn.addEventListener("click", () => chat.toggle());
  muteBtn.addEventListener("click", () => opts.toggleMute());
  endBtn.addEventListener("click", () => opts.end());

  return {
    setAppearance(next) {
      if (appearancePinned || next == null) return;
      if (appearance && avatarHandle.setAppearance) {
        appearance = next;
        avatarHandle.setAppearance(next);
        applyTheme();
        return;
      }
      appearance = next;
      avatarHandle.destroy();
      avatarHandle = createOrbUI(avatarBtn, store, { appearance: next });
      avatarBtn.appendChild(ring);
      applyTheme();
    },
    setTeaser(next) {
      teaser = next;
      teaserText.textContent = next?.text ?? "";
      render(store.getState());
    },
    setTheme(next) {
      if (themePinned || next == null || next === theme) return;
      theme = next;
      applyTheme();
      // The aura shader bakes its theme at construction; rebuild it so a
      // fetched dark widget_theme doesn't leave a light aura behind.
      if (!appearance) {
        avatarHandle.destroy();
        avatarHandle = createAura(avatarBtn, store, {
          color: opts.color,
          themeMode: theme,
        });
        avatarBtn.appendChild(ring);
      }
    },
    destroy() {
      unsub();
      unsubChat();
      avatarHandle.destroy();
      attribution.remove();
      teaserEl.remove();
      container.remove();
      root.removeAttribute("data-moss-theme");
      root.style.removeProperty("--moss-teaser-accent");
    },
  };
}
